import type { TFieldValue } from "../types/form.types";
import type { IFieldConfig, IFormStyleConfig } from "../interface/form.interfaces";
import { AddressData, AddressInput } from "./AddressInput";
import React from "react";
import { Box } from "@mui/material";
import { TextInput, TextareaInput, SelectInput, RadioGroup, CheckboxInput } from "./FormInputs";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";

interface FormFieldProps {
  field: IFieldConfig;
  value: TFieldValue;
  error?: string;
  onChange: (name: string, value: TFieldValue) => void;
  onBlur: (name: string) => void;
  styles?: IFormStyleConfig;

  onAddressSelect?: (addressData: AddressData) => void;
}

export function FormField({ field, value, error, onChange, onBlur, styles, onAddressSelect }: FormFieldProps) {
  const [showPassword, setShowPassword] = React.useState(false);

  const hasError = Boolean(error);

  const fieldOnChange = (val: TFieldValue) => {
    onChange(field.name, val);
  };

  const fieldOnBlur = () => {
    onBlur(field.name);
  };

  const colSpan = field.cols
    ? {
        gridColumn: {
          xs: "span 1",
          md: `span ${field.cols}`,
        },
      }
    : undefined;

  const wrapperStyle = {
    ...styles?.fieldWrapper,
    ...colSpan,
  };

  const getCombinedInputStyle = (specificStyle?: React.CSSProperties): React.CSSProperties => {
    return {
      ...styles?.sharedInputs,
      ...specificStyle,
    };
  };

  // ─────────────────────────────────────────────────────────────
  // CHECKBOX
  // ─────────────────────────────────────────────────────────────

  if (field.type === "checkbox") {
    return (
      <Box className="df-field" sx={wrapperStyle}>
        <CheckboxInput
          id={field.name}
          label={field.label}
          checked={value as boolean}
          required={field.required}
          disabled={field.disabled}
          onChange={fieldOnChange}
          style={getCombinedInputStyle(styles?.checkboxOption)}
        />

        {error && (
          <span className="df-error" role="alert" style={styles?.errorText}>
            {error}
          </span>
        )}
      </Box>
    );
  }

  // ─────────────────────────────────────────────────────────────
  // NORMAL FIELDS
  // ─────────────────────────────────────────────────────────────

  return (
    <Box className="df-field" sx={wrapperStyle}>
      <label id={`${field.name}-label`} className="df-label" htmlFor={field.name} style={styles?.label}>
        {field.label}

        {field.required && (
          <span className="df-required" aria-hidden="true">
            *
          </span>
        )}
      </label>

      {/* TEXTAREA */}
      {field.type === "textarea" && (
        <TextareaInput
          id={field.name}
          value={value as string}
          placeholder={field.placeholder}
          disabled={field.disabled}
          hasError={hasError}
          onChange={fieldOnChange}
          onBlur={fieldOnBlur}
          style={getCombinedInputStyle(styles?.textarea)}
        />
      )}

      {/* SELECT */}
      {field.type === "select" && (
        <SelectInput
          id={field.name}
          value={value as string}
          options={field.options ?? []}
          disabled={field.disabled}
          hasError={hasError}
          onChange={fieldOnChange}
          onBlur={fieldOnBlur}
          style={getCombinedInputStyle(styles?.select)}
        />
      )}

      {/* RADIO */}
      {field.type === "radio" && (
        <RadioGroup
          name={field.name}
          value={value as string}
          options={field.options ?? []}
          disabled={field.disabled}
          onChange={fieldOnChange}
          containerStyle={styles?.radioContainer}
          optionStyle={getCombinedInputStyle(styles?.radioOption)}
        />
      )}

      {/* ADDRESS */}
      {field.type === "address" && (
        <AddressInput
          id={field.name}
          value={value as string}
          placeholder={field.placeholder}
          disabled={field.disabled}
          hasError={hasError}
          onChange={fieldOnChange}
          onBlur={fieldOnBlur}
          onAddressSelect={onAddressSelect}
          autocompleteOptions={field.autocompleteOptions}
          style={getCombinedInputStyle(styles?.address)}
        />
      )}

      {/* TEXT / EMAIL / PASSWORD / NUMBER / DATE */}
      {(["text", "email", "password", "number", "date"] as const).includes(field.type as never) && (
        <TextInput
          id={field.name}
          type={field.type === "password" && showPassword ? "text" : (field.type as "text" | "email" | "password" | "number" | "date")}
          value={value as string | number}
          placeholder={field.placeholder}
          disabled={field.disabled}
          hasError={hasError}
          onChange={fieldOnChange}
          onBlur={fieldOnBlur}
          style={getCombinedInputStyle(styles?.text)}
          rightElement={
            field.type === "password" ? (
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  padding: 0,
                  color: "var(--fx-surface-textsecondary, #64748B)",
                }}
                tabIndex={-1}
                title={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
              </button>
            ) : undefined
          }
        />
      )}

      {/* HELPER */}
      {field.helperText && !error && (
        <span className="df-helper" style={styles?.helperText}>
          {field.helperText}
        </span>
      )}

      {/* ERROR */}
      {error && (
        <span className="df-error" role="alert" style={styles?.errorText}>
          {error}
        </span>
      )}
    </Box>
  );
}
